/**
 * Multi-document tabs + dirty guard for tab switch / tab close / beforeunload.
 * Sidebar and other global navigations do not use confirmLeaveIfDirty yet (see plan).
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle
} from '@mui/material';

const DocumentTabsContext = createContext(null);

/** @returns {{ kind: 'quote'|'order'|'po', defaultTitle: string } | null} */
export function parseTabbedEntity(pathname) {
  if (!pathname) return null;
  if (/^\/quotes\/(\d+|new)$/.test(pathname)) {
    const m = pathname.match(/^\/quotes\/(.+)$/);
    const seg = m ? m[1] : '';
    return {
      kind: 'quote',
      defaultTitle: seg === 'new' ? 'New quote' : `Quote ${seg}`
    };
  }
  if (/^\/work-orders\/\d+$/.test(pathname)) {
    const m = pathname.match(/^\/work-orders\/(\d+)$/);
    return { kind: 'order', defaultTitle: m ? `Order ${m[1]}` : 'Order' };
  }
  if (/^\/procurement\/po\/(\d+|new)$/.test(pathname)) {
    const m = pathname.match(/^\/procurement\/po\/(.+)$/);
    const seg = m ? m[1] : '';
    return {
      kind: 'po',
      defaultTitle: seg === 'new' ? 'New P.O.' : `P.O. ${seg}`
    };
  }
  return null;
}

function fallbackListPath(kind) {
  if (kind === 'quote') return '/quotes';
  if (kind === 'order') return '/work-orders';
  return '/procurement/po';
}

export function DocumentTabsProvider({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [tabs, setTabs] = useState([]);
  const titlesRef = useRef({});
  const dirtyRef = useRef(null);
  const pendingLeaveRef = useRef(null);
  const pendingResolveRef = useRef(null);
  const [leaveDialog, setLeaveDialog] = useState({
    open: false,
    saving: false,
    saveError: ''
  });

  const upsertTabForPath = useCallback((pathname) => {
    const meta = parseTabbedEntity(pathname);
    if (!meta) return;
    setTabs((prev) => {
      let base = prev;
      // First save from /quotes/new (or /procurement/po/new) uses history replace to the real id;
      // remove the stale "new" tab row so two tabs do not appear.
      if (/^\/quotes\/\d+$/.test(pathname)) {
        delete titlesRef.current['/quotes/new'];
        base = base.filter((t) => t.path !== '/quotes/new');
      }
      if (/^\/procurement\/po\/\d+$/.test(pathname)) {
        delete titlesRef.current['/procurement/po/new'];
        base = base.filter((t) => t.path !== '/procurement/po/new');
      }
      const path = pathname;
      const idx = base.findIndex((t) => t.path === path);
      const title = titlesRef.current[path] || meta.defaultTitle;
      const row = { path, kind: meta.kind, title };
      if (idx === -1) return [...base, row];
      const next = [...base];
      next[idx] = { ...next[idx], title };
      return next;
    });
  }, []);

  useEffect(() => {
    upsertTabForPath(location.pathname);
  }, [location.pathname, upsertTabForPath]);

  const registerTabTitle = useCallback((path, title) => {
    if (!path || !title) return;
    titlesRef.current[path] = title;
    setTabs((prev) => prev.map((t) => (t.path === path ? { ...t, title } : t)));
  }, []);

  const registerDirtyState = useCallback((pathKey, entry) => {
    dirtyRef.current = { pathKey, ...entry };
    return () => {
      if (dirtyRef.current?.pathKey === pathKey) {
        dirtyRef.current = null;
      }
    };
  }, []);

  const isActivePathDirty = useCallback(() => {
    const d = dirtyRef.current;
    if (!d || d.pathKey !== location.pathname) return false;
    return typeof d.isDirty === 'function' ? d.isDirty() : !!d.isDirty;
  }, [location.pathname]);

  useEffect(() => {
    const onBeforeUnload = (e) => {
      const d = dirtyRef.current;
      if (!d || d.pathKey !== location.pathname) return;
      const dirty = typeof d.isDirty === 'function' ? d.isDirty() : !!d.isDirty;
      if (!dirty) return;
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [location.pathname]);

  const runPending = useCallback(
    (pending) => {
      if (!pending) return;
      if (pending.type === 'navigate') {
        navigate(pending.to);
        return;
      }
      if (pending.type === 'closeTab') {
        const closingPath = pending.path;
        const isClosingActive = closingPath === location.pathname;
        setTabs((prev) => {
          const next = prev.filter((t) => t.path !== closingPath);
          if (isClosingActive) {
            if (next.length > 0) {
              const last = next[next.length - 1];
              queueMicrotask(() => navigate(last.path));
            } else {
              const meta = parseTabbedEntity(closingPath);
              queueMicrotask(() => navigate(meta ? fallbackListPath(meta.kind) : '/'));
            }
          }
          return next;
        });
      }
    },
    [location.pathname, navigate]
  );

  const finishLeaveDialog = useCallback((proceed) => {
    const res = pendingResolveRef.current;
    pendingResolveRef.current = null;
    pendingLeaveRef.current = null;
    setLeaveDialog({ open: false, saving: false, saveError: '' });
    if (typeof res === 'function') res(proceed);
  }, []);

  const confirmLeaveIfDirty = useCallback(
    (pending) =>
      new Promise((resolve) => {
        const d = dirtyRef.current;
        const dirtyNow =
          d &&
          d.pathKey === location.pathname &&
          (typeof d.isDirty === 'function' ? d.isDirty() : !!d.isDirty);
        if (!dirtyNow) {
          runPending(pending);
          resolve(true);
          return;
        }
        pendingLeaveRef.current = pending;
        pendingResolveRef.current = resolve;
        setLeaveDialog({ open: true, saving: false, saveError: '' });
      }),
    [location.pathname, runPending]
  );

  const closeLeaveDialog = useCallback(() => {
    if (leaveDialog.saving) return;
    finishLeaveDialog(false);
  }, [finishLeaveDialog, leaveDialog.saving]);

  const handleDiscard = useCallback(async () => {
    const pending = pendingLeaveRef.current;
    const d = dirtyRef.current;
    if (d?.discard) await Promise.resolve(d.discard());
    finishLeaveDialog(true);
    runPending(pending);
  }, [finishLeaveDialog, runPending]);

  const handleSaveAndLeave = useCallback(async () => {
    const d = dirtyRef.current;
    if (!d?.save) {
      await handleDiscard();
      return;
    }
    setLeaveDialog((s) => ({ ...s, saving: true, saveError: '' }));
    try {
      await d.save();
      const pending = pendingLeaveRef.current;
      finishLeaveDialog(true);
      runPending(pending);
    } catch (err) {
      const msg = err?.message || err?.response?.data?.message || 'Save failed';
      setLeaveDialog((s) => ({ ...s, saving: false, saveError: String(msg) }));
    }
  }, [finishLeaveDialog, handleDiscard, runPending]);

  const tryNavigate = useCallback(
    (to) => confirmLeaveIfDirty({ type: 'navigate', to }),
    [confirmLeaveIfDirty]
  );

  const tryCloseTab = useCallback(
    (path) => confirmLeaveIfDirty({ type: 'closeTab', path }),
    [confirmLeaveIfDirty]
  );

  const value = useMemo(
    () => ({
      tabs,
      registerTabTitle,
      registerDirtyState,
      tryNavigate,
      tryCloseTab,
      isActivePathDirty
    }),
    [tabs, registerTabTitle, registerDirtyState, tryNavigate, tryCloseTab, isActivePathDirty]
  );

  return (
    <DocumentTabsContext.Provider value={value}>
      {children}
      <Dialog open={leaveDialog.open} onClose={leaveDialog.saving ? undefined : closeLeaveDialog}>
        <DialogTitle>Unsaved changes</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You have unsaved changes. Save before leaving this tab, or discard them.
          </DialogContentText>
          {leaveDialog.saveError ? (
            <DialogContentText color="error" sx={{ mt: 1 }}>
              {leaveDialog.saveError}
            </DialogContentText>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeLeaveDialog} disabled={leaveDialog.saving}>
            Cancel
          </Button>
          <Button
            onClick={() => void handleDiscard()}
            color="warning"
            disabled={leaveDialog.saving}
          >
            Discard changes
          </Button>
          <Button onClick={handleSaveAndLeave} variant="contained" disabled={leaveDialog.saving}>
            {leaveDialog.saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </DocumentTabsContext.Provider>
  );
}

export function useDocumentTabs() {
  const ctx = useContext(DocumentTabsContext);
  if (!ctx) {
    throw new Error('useDocumentTabs must be used within DocumentTabsProvider');
  }
  return ctx;
}
